import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Button,
  ConfigProvider,
  Input,
  Modal,
  Pagination,
  Form,
  Space,
  Table,
  Dropdown,
} from "antd";
import {
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
  Tiles,
} from "@cloudscape-design/components";
import {
  EditOutlined,
  HolderOutlined,
  PlusOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  DownOutlined,
  CheckCircleOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import { NavLink, useNavigate } from "react-router-dom";
import useSectionFilter from "../../table-filters/section-filters";
import { SectionRequest } from "../../redux/api/section/types";
import IncredableContent from "../../components/incredable-content";
import { SpinnerContext } from "../../context/spinner/spinner";
import { ToastContext } from "../../context/toast/incredable-toast";
import {
  useActiveSectionMutation,
  useInActiveSectionMutation,
  useSectionChangeSortOrderMutation,
  useUpdateSectionMutation,
} from "../../redux/api/section/section";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DragEndEvent, DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import IncredableButton from "../../components/button";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../redux/store";
import { capitalize } from "lodash";

export default function SectionsList() {
  const [selectedRow, setSelectedRow] = useState<SectionRequest | null>(null);
  const [section, setSection] = useState<SectionRequest[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editSection, setEditSection] = useState<SectionRequest | null>(null);

  const hasPermission = useSelector(
    (state: ApplicationState) =>
      state.ui.is_owner ||
      (state.ui.permissions?.includes("section.edit") ?? false)
  );
  const [activeTabKey, setActiveTabKey] = useState<string>("provider");
  const onTabChange = useCallback((key: string) => {
    setActiveTabKey(key);
  }, []);

  const {
    TableFilters,
    filteredSection,
    UseQueryHookResult: { refetch, isFetching, isLoading },
  } = useSectionFilter({ activeTabKey });

  useEffect(() => {
    setSection(filteredSection || []);
  }, [filteredSection]);

  const [updateSection] = useUpdateSectionMutation();
  const [sectionChangeSortOrder] = useSectionChangeSortOrderMutation();
  const spinnerContext = useContext(SpinnerContext);
  const toastContext = useContext(ToastContext);
  const [activeSection] = useActiveSectionMutation();
  const [inactiveSection] = useInActiveSectionMutation();
  const navigate = useNavigate();

  const filteredItems = useMemo(() => {
    return filteredSection?.filter(
      (item) =>
        (activeTabKey === "provider" && item.isFacility === false) ||
        (activeTabKey === "facility" && item.isFacility === true)
    );
  }, [filteredSection, activeTabKey]);

  const handleSectionSortOrderUpdate = useCallback(
    async (sortedData: SectionRequest[]) => {
      const payload = sortedData.map((item, index) => ({
        sectionId: Number(item?.id),
        sortOrder: index,
      }));
      try {
        spinnerContext?.showSpinner();
        await sectionChangeSortOrder(payload).unwrap();
        toastContext?.openSuccessNotification({
          message: "Section order updated successfully",
          placement: "topRight",
        });
      } catch (error) {
        toastContext?.openErrorNotification({
          message: "Section order change failed",
          placement: "topRight",
        });
      } finally {
        spinnerContext?.hidespinner();
      }
    },
    [sectionChangeSortOrder, toastContext, spinnerContext]
  );

  const handleSectionEditSave = async (values: {
    name: string;
    description: string;
  }) => {
    setIsEditModalOpen(false);
    if (editSection) {
      try {
        spinnerContext?.showSpinner();
        await updateSection({
          id: String(editSection.id),
          ...editSection,
          ...values,
        }).unwrap();
        toastContext?.openSuccessNotification({
          message: `Section updated successfully`,
          placement: "topRight",
        });
        setEditSection(null);
      } catch (error) {
        toastContext?.openErrorNotification({
          message: `Section update failed`,
          placement: "topRight",
        });
      } finally {
        spinnerContext?.hidespinner();
      }
    }
  };

  const handleSectionDelete = async () => {
    if (selectedRow?.active === true) {
      try {
        setIsDeleteModalOpen(false);
        spinnerContext?.showSpinner();
        await inactiveSection({ id: selectedRow?.id + "" });
        toastContext?.openSuccessNotification({
          message: "Section Inactivated successfully",
          placement: "top",
        });
        setSelectedRow(null);
      } catch (error) {
        toastContext?.openErrorNotification({
          message: "Failed to inactivate section",
          placement: "top",
        });
      } finally {
        spinnerContext?.hidespinner();
      }
    } else if (selectedRow?.active === false) {
      try {
        setIsDeleteModalOpen(false);
        spinnerContext?.showSpinner();
        await activeSection({ id: selectedRow?.id + "" });
        toastContext?.openSuccessNotification({
          message: "Section Activated successfully",
          placement: "top",
        });
        setSelectedRow(null);
      } catch (error) {
        toastContext?.openErrorNotification({
          message: "Failed to activate section",
          placement: "top",
        });
      } finally {
        spinnerContext?.hidespinner();
      }
    }
  };

  return (
    <IncredableContent style={{ margin: "32px 32px 0 32px" }}>
      <SpaceBetween size="l" direction="vertical">
        <Tiles
          onChange={({ detail }) => {
            onTabChange(detail?.value);
          }}
          value={activeTabKey}
          items={[
            {
              label: "Provider FlexFields",
              description:
                "Section for individual provider-specific fields(e.g., licenses, credentials).",
              value: "provider",
            },
            {
              label: "Facility FlexFields",
              description:
                "Section for individual facility-specific fields(e.g., location, type, accrediation).",
              value: "facility",
            },
          ]}
        />
        <Container
          header={
            <Header
              description={`FlexField Section is a collection of related FlexFields, that helps organize and manage custom data fields efficiently within a ${activeTabKey} profile.`}
              actions={
                <Space size="middle">
                  <Button onClick={() => refetch && refetch()}>
                    <ReloadOutlined />
                  </Button>
                  <Dropdown
                    menu={{
                      disabled: !selectedRow,
                      items: [
                        {
                          label: "Edit",
                          key: "edit",
                          disabled: !(
                            hasPermission && selectedRow?.active === true
                          ),
                          icon: <EditOutlined />,
                          onClick: () => {
                            navigate(`${selectedRow?.id}/edit`);
                          },
                        },
                        {
                          label: !selectedRow?.active ? "Active" : "Inactive",
                          key: !selectedRow?.active ? "active" : "inactive",
                          icon: !selectedRow?.active ? (
                            <CheckCircleOutlined />
                          ) : (
                            <MinusCircleOutlined />
                          ),
                          onClick: () => {
                            setIsDeleteModalOpen(true);
                          },
                        },
                      ],
                    }}
                  >
                    <Button>
                      Actions
                      <DownOutlined />
                    </Button>
                  </Dropdown>
                  <ConfigProvider
                    theme={{
                      token: {
                        colorPrimary: "#2B6BE6",
                      },
                    }}
                  >
                    <Button
                      type="primary"
                      disabled={!hasPermission}
                      onClick={() => navigate("add")}
                      icon={<PlusOutlined />}
                    >
                      Add New Section
                    </Button>
                  </ConfigProvider>
                </Space>
              }
            >
              {capitalize(activeTabKey)} FlexField Sections{" "}
            </Header>
          }
        >
          <DragableTable
            dataSource={filteredItems || []}
            onUpdateOrder={handleSectionSortOrderUpdate}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            isFetching={isFetching}
            isLoading={isLoading}
            onEdit={(record) => {
              setEditSection(record);
              setIsEditModalOpen(true);
            }}
            TableFilters={TableFilters}
          />
        </Container>
      </SpaceBetween>
      <Modal
        title="Edit Section"
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={null}
        zIndex={1000}
      >
        {editSection && (
          <Form
            layout="vertical"
            initialValues={{
              name: editSection.name,
              description: editSection.description,
            }}
            onFinish={handleSectionEditSave}
          >
            <Form.Item
              label="Section Name"
              name="name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: "Description is required" }]}
            >
              <Input.TextArea />
            </Form.Item>
            <Form.Item>
              <Space style={{ marginTop: 16 }}>
                <Button onClick={() => setIsEditModalOpen(false)}>
                  Cancel
                </Button>
                <IncredableButton
                  style={{ backgroundColor: "#598BEB" }}
                  type="primary"
                  htmlType="submit"
                >
                  Save
                </IncredableButton>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: "#faad14" }} />
            {selectedRow?.active === true
              ? "Deactivate Section"
              : "Activate Section"}
          </Space>
        }
        open={isDeleteModalOpen}
        onOk={handleSectionDelete}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setSelectedRow(null);
        }}
        okText="Save"
        okButtonProps={{ danger: true }}
        zIndex={1000}
      >
        <p>
          Are you sure you want to{" "}
          {selectedRow?.active === true ? "deactivate" : "activate"} this
          section "{selectedRow?.name}"?
        </p>
      </Modal>
    </IncredableContent>
  );
}

interface DragableTableProps {
  dataSource: SectionRequest[];
  onUpdateOrder: (newAttributes: SectionRequest[]) => void;
  selectedRow: SectionRequest | null;
  setSelectedRow: React.Dispatch<React.SetStateAction<SectionRequest | null>>;
  isLoading: boolean;
  isFetching: boolean;
  onEdit: (record: SectionRequest) => void;
  TableFilters?: React.ReactNode;
}

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: any;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: "move" }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const DragableTable: React.FC<DragableTableProps> = ({
  dataSource,
  onUpdateOrder,
  selectedRow,
  setSelectedRow,
  isLoading,
  isFetching,
  TableFilters
}) => {
  const [tableData, setTableData] = useState<SectionRequest[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const sorted = [...dataSource].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    setTableData(sorted.map((item, index) => ({ ...item, sortOrder: index })));
  }, [dataSource]);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return tableData.slice(start, start + pageSize);
  }, [tableData, currentPage]);

  const onDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeIndex = paginatedItems.findIndex((i) => String(i.id) === active.id);
    const overIndex = paginatedItems.findIndex((i) => String(i.id) === over.id);
    if (activeIndex < 0 || overIndex < 0) return;
    const updatedPage = arrayMove(paginatedItems, activeIndex, overIndex);
    const newData = [...tableData];
    const start = (currentPage - 1) * pageSize;
    for (let i = 0; i < updatedPage.length; i++) {
      newData[start + i] = updatedPage[i];
    }
    const reIndexed = newData.map((item, index) => ({
      ...item,
      sortOrder: index,
    }));

    setTableData(reIndexed);
    onUpdateOrder(reIndexed);
  }, [paginatedItems, tableData, currentPage, onUpdateOrder]);

  const columns = [
    {
      key: "sort",
      align: "center" as const,
      width: 80,
      render: () => <DragHandle />,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text: string, record: SectionRequest) => (
        <span>
          {" "}
          {record?.active === true ? (
            <NavLink to={`${record.id}/edit`}>{text}</NavLink>
          ) : (
            <span>{text}</span>
          )}
        </span>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Type",
      dataIndex: "isFacility",
      render: (text: string, record: SectionRequest) => (
        <span>{record?.isFacility === false ? "Provider" : "Facility"}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "active",
      render: (text: string, record: SectionRequest) => (
        <span>
          <StatusIndicator colorOverride={record?.active ? "green" : "red"}>
            {record?.active ? "In-use" : "Inactive"}
          </StatusIndicator>
        </span>
      ),
    },
  ];

  return (
    <>
      <Space
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: "10px",
        }}
      >
        {TableFilters}
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={tableData.length}
          onChange={setCurrentPage}
          showSizeChanger={false}
        />
      </Space>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={paginatedItems.map((item) => String(item.id))}
          strategy={verticalListSortingStrategy}
        >
          <Table
            rowKey="id"
            loading={isLoading || isFetching}
            dataSource={paginatedItems}
            columns={columns}
            pagination={false}
            bordered
            size="small"
            components={{ body: { row: Row } }}
            onRow={(record) => ({
              onClick: () => setSelectedRow(record),
              style: {
                backgroundColor: selectedRow?.id === record.id ? "#d9d9d9" : undefined,
              },
            })}
          />
        </SortableContext>
      </DndContext>

    </>
  );
};

const Row: React.FC<
  { "data-row-key": string } & React.HTMLAttributes<HTMLTableRowElement>
> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    isDragging,
  } = useSortable({ id: String(props["data-row-key"]) });

  const style: React.CSSProperties = useMemo(
    () => ({
      ...props.style,
      transform: CSS.Transform.toString(transform),
      transition: isDragging ? "none" : "transform 0.2s ease",
      zIndex: isDragging ? 100 : "auto",
      background: isDragging ? "#fafafa" : undefined,
    }),
    [props.style, transform, isDragging]
  );

  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};